import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useOnboardingFlow } from './useOnboardingFlow';
import { STEP_DEFINITIONS } from './onboardingQuestions';
import ProgressBar from './shared/ProgressBar';
import './Onboarding.css';

// Step components
import NameStep from './steps/NameStep';
import DateOfBirthStep from './steps/DateOfBirthStep';
import GenderStep from './steps/GenderStep';
import ExperienceLevelStep from './steps/ExperienceLevelStep';
import TrainingPurposeStep from './steps/TrainingPurposeStep';
import ProgrammeTypeStep from './steps/ProgrammeTypeStep';
import TrainingDistanceStep from './steps/TrainingDistanceStep';
import RaceConfirmationStep from './steps/RaceConfirmationStep';
import RaceSelectionStep from './steps/RaceSelectionStep';
import RaceDateStep from './steps/RaceDateStep';
import TrainingDurationStep from './steps/TrainingDurationStep';
import TrainingStartDateStep from './steps/TrainingStartDateStep';
import GoalTimeStep from './steps/GoalTimeStep';
import CurrentPBStep from './steps/CurrentPBStep';
import RecentRunHistoryStep from './steps/RecentRunHistoryStep';
import LongestRunTimeStep from './steps/LongestRunTimeStep';
import WeeklyFrequencyStep from './steps/WeeklyFrequencyStep';
import TrainingAvailabilityStep from './steps/TrainingAvailabilityStep';
import LongRunDayStep from './steps/LongRunDayStep';
import TrainingIntensityStep from './steps/TrainingIntensityStep';
import IntensiveDaysStep from './steps/IntensiveDaysStep';
import DistanceUnitStep from './steps/DistanceUnitStep';
import RunningCommunityStep from './steps/RunningCommunityStep';
import RunClubDayStep from './steps/RunClubDayStep';
import RunClubIntensityStep from './steps/RunClubIntensityStep';
import RunClubDistanceStep from './steps/RunClubDistanceStep';
import AdditionalInfoStep from './steps/AdditionalInfoStep';
import SummaryStep from './steps/SummaryStep';

const STEP_COMPONENTS: Record<string, React.FC<any>> = {
  initial_onboarding: NameStep,
  date_of_birth: DateOfBirthStep,
  gender: GenderStep,
  experience_level: ExperienceLevelStep,
  training_purpose: TrainingPurposeStep,
  programme_type: ProgrammeTypeStep,
  training_distance: TrainingDistanceStep,
  race_training_confirmation: RaceConfirmationStep,
  race_selection: RaceSelectionStep,
  race_date: RaceDateStep,
  training_duration: TrainingDurationStep,
  training_start_date: TrainingStartDateStep,
  goal_time: GoalTimeStep,
  current_pb: CurrentPBStep,
  recent_running_history: RecentRunHistoryStep,
  recent_longest_run_time: LongestRunTimeStep,
  weekly_training_frequency: WeeklyFrequencyStep,
  training_availability: TrainingAvailabilityStep,
  long_run_day_preference: LongRunDayStep,
  training_intensity_preference: TrainingIntensityStep,
  intensive_workout_days_preference: IntensiveDaysStep,
  distance_unit_preference: DistanceUnitStep,
  running_community_participation: RunningCommunityStep,
  run_club_day: RunClubDayStep,
  run_club_intensity: RunClubIntensityStep,
  run_club_distance: RunClubDistanceStep,
  additional_information: AdditionalInfoStep,
  summary: SummaryStep,
};

// Steps that auto-advance on selection (single-select option steps)
const AUTO_ADVANCE_STEPS = new Set([
  'gender',
  'experience_level',
  'training_purpose',
  'programme_type',
  'race_training_confirmation',
  'race_selection',
  'long_run_day_preference',
  'training_intensity_preference',
  'distance_unit_preference',
  'running_community_participation',
  'run_club_day',
  'run_club_intensity',
]);

const Onboarding: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const flow = useOnboardingFlow();

  useEffect(() => {
    if (currentUser) {
      navigate('/offers');
    }
  }, [currentUser, navigate]);

  const stepDef = STEP_DEFINITIONS[flow.currentStepId];
  const StepComponent = STEP_COMPONENTS[flow.currentStepId];

  const isAutoAdvance = AUTO_ADVANCE_STEPS.has(flow.currentStepId);

  // For auto-advance steps: store + advance in one call
  // For manual steps: store only, user clicks Continue to advance
  const handleAnswer = (value: any) => {
    if (isAutoAdvance) {
      flow.answerAndAdvance(value);
    } else {
      flow.setAnswer(value);
    }
  };

  const handleContinue = () => {
    if (flow.isLastStep) {
      const sessionData = flow.getOnboardingSessionData();
      navigate('/signup', { state: { onboardingData: sessionData } });
      return;
    }
    // Advance to next step
    flow.advance();
  };

  const handleSkip = () => {
    flow.answerAndAdvance(null);
  };

  // Determine if Continue button should show
  const needsContinue =
    !isAutoAdvance &&
    flow.currentStepId !== 'summary' &&
    flow.isAnswered;

  const showSummaryContinue = flow.currentStepId === 'summary';

  return (
    <div className="auth-container">
      <div className="onboarding-content">
        {/* Top bar */}
        <div className="onboarding-top-bar">
          <button
            className="onboarding-back-btn"
            onClick={flow.canGoBack ? flow.goBack : () => navigate('/')}
            type="button"
          >
            ← Back
          </button>
          <ProgressBar progress={flow.progress} />
        </div>

        {/* Step header */}
        <div className="onboarding-header">
          <h2 className="onboarding-step-title">{stepDef.question}</h2>
          {stepDef.hintText && (
            <p className="onboarding-step-hint">{stepDef.hintText}</p>
          )}
        </div>

        {/* Step content */}
        <div className="onboarding-step-content">
          {StepComponent && (
            <StepComponent
              answer={flow.currentAnswer}
              onAnswer={handleAnswer}
              answers={flow.answers}
            />
          )}
        </div>

        {/* Bottom actions */}
        <div className="onboarding-actions">
          {needsContinue && (
            <button className="btn-primary" onClick={handleContinue} type="button">
              Continue
            </button>
          )}

          {showSummaryContinue && (
            <button className="btn-primary" onClick={handleContinue} type="button">
              Create your account
            </button>
          )}

          {flow.canSkip && !flow.isAnswered && (
            <button className="onboarding-skip-btn" onClick={handleSkip} type="button">
              Skip
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
